<template>
  <section class="honoree-form">
    <h2 class="honoree-form__title">
      {{ translations.title }}
    </h2>
    <form ref="form" @submit.prevent="handleFormSubmit">
      <!-- Are you nominating yourself for this award? -->
      <fieldset class="honoree-form__subsection">
        <legend>{{ translations.areYouNominatingYourself.label }}</legend>
        <div class="honoree-form__radio-group">
          <label for="nominating-myself-yes" class="honoree-form__checkbox__wrapper">
            <input
              id="nominating-myself-yes"
              v-model="isSelf"
              class="honoree-form__checkbox__input"
              type="radio"
              name="are-you-nominating-yourself"
              :value="true"
            />
            <span class="honoree-form__checkbox__checkmark" />
            {{ translations.areYouNominatingYourself.answers.yes.label }}
          </label>
          <label for="nominating-myself-no" class="honoree-form__checkbox__wrapper">
            <input
              id="nominating-myself-no"
              v-model="isSelf"
              class="honoree-form__checkbox__input"
              type="radio"
              name="are-you-nominating-yourself"
              :value="false"
            />
            <span class="honoree-form__checkbox__checkmark" />
            {{ translations.areYouNominatingYourself.answers.no.label }}
          </label>
        </div>
      </fieldset>

      <hr class="honoree-form__hr" />

      <!-- nominatorInfo block -->
      <transition name="slide-fade">
        <fieldset v-if="!isSelf" class="honoree-form__subsection row">
          <legend class="honoree-form__subsection__title honoree-form__subsection__title col-lg-12">
            {{ translations.fieldSections.yourContactInfoSection.title }}
          </legend>
          <!-- title -->
          <HonoreeFormSelectField
            id="my-title"
            v-model="formData.nominatorInfo.title"
            :model-value="formData.nominatorInfo.title"
            class="col-lg-2"
            :label="translations.fieldSections.yourContactInfoSection.fields.title.label"
            :initial-data="initialData.data.yourContactInfo.title"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.title.requiredFieldMessage
            "
            :options="settings.titleOptions"
            :default-option="settings.selectDefaultOption"
            @update:modelValue="(newValue) => (formData.nominatorInfo.title = newValue)"
          />
          <!-- firstName -->
          <HonoreeFormInputField
            id="my-first-name"
            v-model="formData.nominatorInfo.firstName"
            :model-value="formData.nominatorInfo.firstName"
            class="col-lg-4"
            :label="translations.fieldSections.yourContactInfoSection.fields.firstName.label"
            :initial-data="initialData.data.yourContactInfo.firstName"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.firstName
                .requiredFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.firstName.maxLength.count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.firstName.maxLength.message
            "
            @update:modelValue="(newValue) => (formData.nominatorInfo.firstName = newValue)"
          />
          <!-- lastName -->
          <HonoreeFormInputField
            id="my-last-name"
            v-model="formData.nominatorInfo.lastName"
            :model-value="formData.nominatorInfo.lastName"
            class="col-lg-6"
            :label="translations.fieldSections.yourContactInfoSection.fields.lastName.label"
            :initial-data="initialData.data.yourContactInfo.lastName"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.lastName.requiredFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.lastName.maxLength.count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.lastName.maxLength.message
            "
            @update:modelValue="(newValue) => (formData.nominatorInfo.lastName = newValue)"
          />
          <!-- primaryPhone -->
          <HonoreeFormInputField
            id="my-primary-phone"
            v-model="formData.nominatorInfo.primaryPhone"
            :model-value="formData.nominatorInfo.primaryPhone"
            class="col-lg-4"
            :label="translations.fieldSections.yourContactInfoSection.fields.primaryPhone.label"
            :initial-data="initialData.data.yourContactInfo.primaryPhone"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.primaryPhone
                .requiredFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.primaryPhone.maxLength.count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.primaryPhone.maxLength
                .message
            "
            @update:modelValue="(newValue) => (formData.nominatorInfo.primaryPhone = newValue)"
          />
          <!-- organizationName -->
          <HonoreeFormInputField
            id="my-organization-name"
            v-model="formData.nominatorInfo.organizationName"
            :model-value="formData.nominatorInfo.organizationName"
            class="col-lg-12"
            :label="translations.fieldSections.yourContactInfoSection.fields.organizationName.label"
            :initial-data="initialData.data.yourContactInfo.organizationName"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.organizationName
                .requiredFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.organizationName.maxLength
                .count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.organizationName.maxLength
                .message
            "
            :is-required="false"
            @update:modelValue="(newValue) => (formData.nominatorInfo.organizationName = newValue)"
          />
          <!-- jobTitle -->
          <HonoreeFormInputField
            id="my-job-title"
            v-model="formData.nominatorInfo.jobTitle"
            :model-value="formData.nominatorInfo.jobTitle"
            class="col-lg-6"
            :label="translations.fieldSections.yourContactInfoSection.fields.jobTitle.label"
            :initial-data="initialData.data.yourContactInfo.jobTitle"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.jobTitle.requiredFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.jobTitle.maxLength.count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.jobTitle.maxLength.message
            "
            :is-required="false"
            @update:modelValue="(newValue) => (formData.nominatorInfo.jobTitle = newValue)"
          />
          <!-- email -->
          <HonoreeFormInputField
            id="my-email-address"
            v-model="formData.nominatorInfo.email"
            :model-value="formData.nominatorInfo.email"
            class="col-lg-6"
            :label="translations.fieldSections.yourContactInfoSection.fields.emailAddress.label"
            :initial-data="initialData.data.yourContactInfo.emailAddress"
            :error-message="
              translations.fieldSections.yourContactInfoSection.fields.emailAddress
                .requiredFieldMessage
            "
            :is-email="true"
            :invalid-message="
              translations.fieldSections.yourContactInfoSection.fields.emailAddress
                .invalidFieldMessage
            "
            :character-limit="
              translations.fieldSections.yourContactInfoSection.fields.emailAddress.maxLength.count
            "
            :max-character-message="
              translations.fieldSections.yourContactInfoSection.fields.emailAddress.maxLength
                .message
            "
            @update:modelValue="(newValue) => (formData.nominatorInfo.email = newValue)"
          />

          <!-- nominee relation section start -->
          <fieldset class="honoree-form__subsection">
            <legend class="wow-nomination-form-legend-hide"></legend>
            <div class="col-lg-12">
              <span
                class="honoree-form__subsection__subtitle honoree-form__subsection__subtitle--paragraph"
              >
                {{ translations.fieldSections.nomineeRelationSection.question }}
              </span>

              <!-- nominee relation section radio buttons start -->
              <div class="honoree-form__radio-group -vertical">
                <label
                  v-for="(answer, i) in nomineeRelationOptions"
                  :key="answer.id"
                  class="honoree-form__checkbox__wrapper -nest"
                >
                  <input
                    :id="answer.id"
                    v-model="honoreeRelated"
                    class="honoree-form__checkbox__input"
                    name="honoree-related"
                    type="radio"
                    :value="answer.value"
                    @change="updateNomineeRelationSectionRadioButtonsState"
                  />
                  <span class="honoree-form__checkbox__checkmark" />
                  {{ answer.label }}
                  <HonoreeFormInputField
                    v-if="
                      otherRelatedVisible &&
                      i === translations.fieldSections.nomineeRelationSection.answers.length - 1
                    "
                    id="pleaseDescribe"
                    v-model="formData.nomineeInfo.pleaseDescribe"
                    class="col-lg-3 -nested"
                    :label="pleaseDescribeLabel"
                    :initial-data="initialData.data.nomineeContactInfo.pleaseDescribe"
                    :error-message="pleaseDescribeError"
                    :character-limit="
                      translations.fieldSections.nomineeRelationSection.maxLength.count
                    "
                    :max-character-message="
                      translations.fieldSections.nomineeRelationSection.maxLength.message
                    "
                  />
                </label>
              </div>
              <!-- nominee relation section radio buttons end-->
            </div>
            <span
              v-if="!nomineeRelationSectionRadioButtonIsValid"
              id="personal-page-related-section--error"
              class="honoree-form__error-state"
            >
              {{ translations.fieldSections.nomineeRelationSection.requiredFieldMessage }}
            </span>
          </fieldset>
          <!-- nominee relation section end -->

          <hr class="honoree-form__hr col-lg-12" />
        </fieldset>
      </transition>

      <!-- nomineeInfo block -->
      <fieldset class="honoree-form__subsection row">
        <legend class="honoree-form__subsection__title honoree-form__subsection__title col-lg-12">
          {{ translations.fieldSections.contactInfoSection.title }}
        </legend>
        <!-- title -->
        <HonoreeFormSelectField
          id="honoree-title"
          v-model="formData.nomineeInfo.title"
          class="col-lg-2"
          :label="translations.fieldSections.contactInfoSection.fields.title.label"
          :initial-data="initialData.data.nomineeContactInfo.title"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.title.requiredFieldMessage
          "
          :options="settings.titleOptions"
          :default-option="settings.selectDefaultOption"
        />
        <!-- firstName -->
        <HonoreeFormInputField
          id="honoree-first-name"
          v-model="formData.nomineeInfo.firstName"
          class="col-lg-4"
          :label="translations.fieldSections.contactInfoSection.fields.firstName.label"
          :initial-data="initialData.data.nomineeContactInfo.firstName"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.firstName.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.firstName.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.firstName.maxLength.message
          "
        />
        <!-- lastName -->
        <HonoreeFormInputField
          id="honoree-last-name"
          v-model="formData.nomineeInfo.lastName"
          class="col-lg-6"
          :label="translations.fieldSections.contactInfoSection.fields.lastName.label"
          :initial-data="initialData.data.nomineeContactInfo.lastName"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.lastName.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.lastName.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.lastName.maxLength.message
          "
        />
        <!-- primaryPhone -->
        <HonoreeFormInputField
          id="honoree-primary-phone"
          v-model="formData.nomineeInfo.primaryPhone"
          class="col-lg-4"
          :label="translations.fieldSections.contactInfoSection.fields.primaryPhone.label"
          :initial-data="initialData.data.nomineeContactInfo.primaryPhone"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.primaryPhone.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.primaryPhone.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.primaryPhone.maxLength.message
          "
        />
        <!-- city -->
        <HonoreeFormInputField
          id="honoree-city"
          v-model="formData.nomineeInfo.city"
          class="col-lg-6"
          :label="translations.fieldSections.contactInfoSection.fields.city.label"
          :initial-data="initialData.data.nomineeContactInfo.city"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.city.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.city.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.city.maxLength.message
          "
        />
        <!-- stateCode -->
        <HonoreeFormSelectField
          id="honoree-state"
          v-model="formData.nomineeInfo.stateCode"
          class="col-lg-3"
          :label="translations.fieldSections.contactInfoSection.fields.state.label"
          :initial-data="initialData.data.nomineeContactInfo.state"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.state.requiredFieldMessage
          "
          :options="settings.stateCodes"
          :default-option="settings.selectDefaultOption"
        />
        <!-- zip -->
        <HonoreeFormInputField
          id="honoree-zip-code"
          v-model="formData.nomineeInfo.zip"
          class="col-lg-3"
          :label="translations.fieldSections.contactInfoSection.fields.zipCode.label"
          :initial-data="initialData.data.nomineeContactInfo.zipCode"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.zipCode.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.zipCode.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.zipCode.maxLength.message
          "
        />
        <HonoreeFormInputField
          id="honoree-email-address"
          v-model="formData.nomineeInfo.email"
          class="col-lg-6"
          :label="translations.fieldSections.contactInfoSection.fields.emailAddress.label"
          :initial-data="initialData.data.nomineeContactInfo.emailAddress"
          :is-email="true"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.emailAddress.requiredFieldMessage
          "
          :invalid-message="
            translations.fieldSections.contactInfoSection.fields.emailAddress.invalidFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.emailAddress.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.emailAddress.maxLength.message
          "
        />
        <HonoreeFormSelectField
          id="honoree-age-range"
          v-model="formData.nomineeInfo.ageRangeId"
          class="col-lg-4"
          :is-required="true"
          :label="translations.fieldSections.contactInfoSection.fields.ageRange.label"
          :initial-data="initialData.data.nomineeContactInfo.ageRange"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.ageRange.requiredFieldMessage
          "
          :options="settings.ageRangeOptions"
          :default-option="settings.selectDefaultOption"
        />

        <!-- Organization fields start -->
        <HonoreeFormInputField
          id="organization-name"
          v-model="formData.nomineeInfo.organizationName"
          class="col-lg-12"
          :label="translations.organizationDetails.organizationName.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationName"
          :is-required="true"
          :error-message="translations.organizationDetails.organizationName.requiredFieldMessage"
          :character-limit="translations.organizationDetails.organizationName.maxLength.count"
          :max-character-message="
            translations.organizationDetails.organizationName.maxLength.message
          "
        />

        <div class="honoree-form__text-group col-lg-12">
          <fieldset class="honoree-form__subsection">
            <legend
              class="honoree-form__subsection__title honoree-form__subsection__title--paragraph"
            >
              {{ translations.fieldSections.organizationNonprofit501c3Section.question }}
            </legend>
            <div class="honoree-form__radio-group">
              <label for="organization-nonprofit-501c3-yes" class="honoree-form__checkbox__wrapper">
                <input
                  id="organization-nonprofit-501c3-yes"
                  v-model="formData.nomineeInfo.organizationNonprofit501c3"
                  class="honoree-form__checkbox__input"
                  name="organization-nonprofit-501c3"
                  type="radio"
                  :value="true"
                />
                <span class="honoree-form__checkbox__checkmark" />
                {{ translations.fieldSections.organizationNonprofit501c3Section.options.yes.label }}
              </label>
              <label for="organization-nonprofit-501c3-no" class="honoree-form__checkbox__wrapper">
                <input
                  id="organization-nonprofit-501c3-no"
                  v-model="formData.nomineeInfo.organizationNonprofit501c3"
                  class="honoree-form__checkbox__input"
                  name="organization-nonprofit-501c3"
                  type="radio"
                  :value="false"
                />
                <span class="honoree-form__checkbox__checkmark" />
                {{ translations.fieldSections.organizationNonprofit501c3Section.options.no.label }}
              </label>
            </div>
            <transition v-if="!formData.nomineeInfo.serviceNow" name="slide-fade">
              <p class="honoree-form__warning-message">
                {{ translations.fieldSections.currentStatusSection.edge.label }}
              </p>
            </transition>
          </fieldset>
        </div>

        <HonoreeFormInputField
          id="organization-website"
          v-model="formData.nomineeInfo.organizationWebsite"
          class="col-lg-12"
          :label="translations.organizationDetails.organizationWebsite.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationWebsite"
          :is-required="true"
          :error-message="translations.organizationDetails.organizationWebsite.requiredFieldMessage"
          :character-limit="translations.organizationDetails.organizationWebsite.maxLength.count"
          :max-character-message="
            translations.organizationDetails.organizationWebsite.maxLength.message
          "
        />

        <!-- nominee organization role section start -->
        <div class="honoree-form__text-group col-lg-12">
          <fieldset class="honoree-form__subsection">
            <legend class="wow-nomination-form-legend-hide"></legend>
            <div class="col-lg-12">
              <span
                class="honoree-form__subsection__subtitle honoree-form__subsection__subtitle--paragraph"
              >
                {{ translations.fieldSections.nomineeOrganizationRoleSection.question }}
              </span>

              <!-- nominee organization role section radio buttons start -->
              <div class="honoree-form__radio-group -vertical">
                <label
                  v-for="(answer, i) in translations.fieldSections.nomineeOrganizationRoleSection
                    .answers"
                  :key="answer.id"
                  class="honoree-form__checkbox__wrapper -nest"
                >
                  <input
                    :id="answer.id"
                    v-model="nomineeOrganizationRoleModel"
                    class="honoree-form__checkbox__input"
                    name="nominee-organization-role"
                    type="radio"
                    :value="answer.value"
                    :aria-invalid="!nomineeOrganizationRoleSectionRadioButtonIsValid"
                    @change="updateNomineeOrganizationRoleRadioButtonsState"
                  />
                  <span class="honoree-form__checkbox__checkmark" />
                  {{ answer.label }}
                  <HonoreeFormInputField
                    v-if="
                      otherNomineeOrganizationRoleIsVisible &&
                      i ===
                        translations.fieldSections.nomineeOrganizationRoleSection.answers.length - 1
                    "
                    id="please-describe-organization-role-other"
                    v-model="formData.nomineeInfo.organizationRoleOtherInputFieldValue"
                    :label="pleaseDescribeLabel"
                    :initial-data="
                      initialData.data.nomineeContactInfo.organizationRoleOtherInputFieldValue
                    "
                    :error-message="pleaseDescribeError"
                    :character-limit="
                      translations.fieldSections.nomineeOrganizationRoleSection.maxLength.count
                    "
                    :max-character-message="
                      translations.fieldSections.nomineeOrganizationRoleSection.maxLength.message
                    "
                    class="col-lg-3 -nested"
                  />
                </label>
              </div>
              <!-- nominee organization role section radio buttons end-->
            </div>
            <span
              v-if="!nomineeOrganizationRoleSectionRadioButtonIsValid"
              id="personal-page-related-section--error"
              class="honoree-form__error-state"
            >
              {{ translations.fieldSections.nomineeRelationSection.requiredFieldMessage }}
            </span>
          </fieldset>
        </div>
        <!-- nominee organization role section end -->

        <div class="honoree-form__text-group col-lg-12">
          <fieldset class="honoree-form__subsection">
            <legend
              class="honoree-form__subsection__title honoree-form__subsection__title--paragraph"
            >
              {{ translations.fieldSections.organizationFounderOrCoFounderSection.question }}
            </legend>
            <div class="honoree-form__radio-group">
              <label
                for="organization-founder-or-cofounder-yes"
                class="honoree-form__checkbox__wrapper"
              >
                <input
                  id="organization-founder-or-cofounder-yes"
                  v-model="formData.nomineeInfo.organizationFounderOrCoFounder"
                  class="honoree-form__checkbox__input"
                  name="organization-founder-or-cofounder"
                  type="radio"
                  :value="true"
                />
                <span class="honoree-form__checkbox__checkmark" />
                {{
                  translations.fieldSections.organizationFounderOrCoFounderSection.options.yes.label
                }}
              </label>
              <label
                for="organization-founder-or-cofounder-no"
                class="honoree-form__checkbox__wrapper"
              >
                <input
                  id="organization-founder-or-cofounder-no"
                  v-model="formData.nomineeInfo.organizationFounderOrCoFounder"
                  class="honoree-form__checkbox__input"
                  name="organization-founder-or-cofounder"
                  type="radio"
                  :value="false"
                />
                <span class="honoree-form__checkbox__checkmark" />
                {{
                  translations.fieldSections.organizationFounderOrCoFounderSection.options.no.label
                }}
              </label>
            </div>
            <transition v-if="!formData.nomineeInfo.serviceNow" name="slide-fade">
              <p class="honoree-form__warning-message">
                {{ translations.fieldSections.currentStatusSection.edge.label }}
              </p>
            </transition>
          </fieldset>
        </div>

        <HonoreeFormInputField
          id="organization-phone"
          v-model="formData.nomineeInfo.organizationPhone"
          :label="translations.organizationDetails.organizationPhone.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationPhone"
          :is-required="true"
          :error-message="translations.organizationDetails.organizationPhone.requiredFieldMessage"
          :character-limit="translations.organizationDetails.organizationPhone.maxLength.count"
          :max-character-message="
            translations.organizationDetails.organizationPhone.maxLength.message
          "
          class="col-lg-6"
        />

        <HonoreeFormInputField
          id="organization-city"
          v-model="formData.nomineeInfo.organizationCity"
          :label="translations.organizationDetails.organizationCity.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationCity"
          :is-required="true"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.city.requiredFieldMessage
          "
          :character-limit="
            translations.fieldSections.contactInfoSection.fields.city.maxLength.count
          "
          :max-character-message="
            translations.fieldSections.contactInfoSection.fields.city.maxLength.message
          "
          class="col-lg-6"
        />

        <HonoreeFormSelectField
          id="organization-state"
          v-model="formData.nomineeInfo.organizationState"
          :label="translations.organizationDetails.organizationState.label"
          :initial-data="
            initialData.data.nomineeContactInfo.organizationState
              ? initialData.data.nomineeContactInfo.organizationState
              : ''
          "
          :is-required="true"
          :error-message="translations.organizationDetails.organizationState.requiredFieldMessage"
          :options="settings.stateCodes"
          :default-option="settings.selectDefaultOption"
          class="col-lg-6"
        />

        <HonoreeFormInputField
          id="organization-social-media-link"
          v-model="formData.nomineeInfo.organizationPrimarySocialMediaLink"
          :label="translations.organizationDetails.organizationPrimarySocialMediaLink.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationPrimarySocialMediaLink"
          :is-required="false"
          :error-message="
            translations.organizationDetails.organizationPrimarySocialMediaLink.requiredFieldMessage
          "
          :character-limit="
            translations.organizationDetails.organizationPrimarySocialMediaLink.maxLength.count
          "
          :max-character-message="
            translations.organizationDetails.organizationPrimarySocialMediaLink.maxLength.message
          "
          class="col-lg-6"
        />

        <HonoreeFormSelectField
          id="organization-annual-budget"
          v-model="formData.nomineeInfo.organizationAnnualBudget"
          :label="
            translations.fieldSections.contactInfoSection.fields.organizationAnnualBudget.label
          "
          :initial-data="initialData.data.nomineeContactInfo.organizationAnnualBudget"
          :is-required="true"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.organizationAnnualBudget
              .requiredFieldMessage
          "
          :options="settings.organizationAnnualBudgetOptions"
          :default-option="settings.selectDefaultOption"
          class="col-lg-4"
        />

        <HonoreeFormSelectField
          id="organization-number-of-paid-staff"
          v-model="formData.nomineeInfo.organizationNumberOfPaidStaff"
          :label="
            translations.fieldSections.contactInfoSection.fields.organizationNumberOfPaidStaff.label
          "
          :initial-data="initialData.data.nomineeContactInfo.organizationNumberOfPaidStaff"
          :is-required="true"
          :error-message="
            translations.fieldSections.contactInfoSection.fields.organizationNumberOfPaidStaff
              .requiredFieldMessage
          "
          :options="settings.organizationNumberOfPaidStaffOptions"
          :default-option="settings.selectDefaultOption"
          class="col-lg-4"
        />

        <HonoreeFormTextAreaField
          id="organization-mission-description"
          v-model="formData.nomineeInfo.organizationDescription"
          :label="translations.organizationDetails.organizationMissionOrPurpose.label"
          :initial-data="initialData.data.nomineeContactInfo.organizationDescription"
          :is-required="true"
          :character-counter="
            translations.organizationDetails.organizationMissionOrPurpose.helpText
          "
          :error-message="
            translations.organizationDetails.organizationMissionOrPurpose.requiredFieldMessage
          "
          class="col-lg-12"
        />

        <!-- Organization fields end -->
      </fieldset>

      <hr class="honoree-form__hr" />

      <fieldset class="honoree-form__subsection">
        <legend class="honoree-form__subsection__title honoree-form__subsection__title--paragraph">
          {{ translations.fieldSections.currentStatusSection.question }}
        </legend>
        <span
          v-for="(answer, $key) in translations.fieldSections.currentStatusSection.answers"
          :key="$key"
          class="honoree-form__subsection__subtitle"
        >
          {{ answer }}
        </span>
        <div class="honoree-form__radio-group">
          <label for="current-status-yes" class="honoree-form__checkbox__wrapper">
            <input
              id="current-status-yes"
              v-model="formData.nomineeInfo.serviceNow"
              class="honoree-form__checkbox__input"
              name="current-status"
              type="radio"
              :value="true"
            />
            <span class="honoree-form__checkbox__checkmark" />
            {{ translations.fieldSections.currentStatusSection.options.yes.label }}
          </label>
          <label for="current-status-no" class="honoree-form__checkbox__wrapper">
            <input
              id="current-status-no"
              v-model="formData.nomineeInfo.serviceNow"
              class="honoree-form__checkbox__input"
              name="current-status"
              type="radio"
              :value="false"
            />
            <span class="honoree-form__checkbox__checkmark" />
            {{ translations.fieldSections.currentStatusSection.options.no.label }}
          </label>
        </div>
        <transition v-if="!formData.nomineeInfo.serviceNow" name="slide-fade">
          <p class="honoree-form__warning-message">
            {{ translations.fieldSections.currentStatusSection.edge.label }}
          </p>
        </transition>
      </fieldset>

      <hr class="honoree-form__hr" />

      <fieldset class="honoree-form__subsection row">
        <legend
          class="honoree-form__subsection__title honoree-form__subsection__title--paragraph col-lg-12"
        >
          {{ translations.fieldSections.timelineSubSection.startingInfo.question }}
        </legend>
        <span
          v-for="(answer, $key) in translations.fieldSections.timelineSubSection.startingInfo
            .answers"
          :key="$key"
          class="honoree-form__subsection__subtitle col-lg-12"
        >
          {{ answer }}
        </span>

        <HonoreeFormSelectField
          id="starting-month"
          v-model="formData.nomineeInfo.serviceMonth"
          class="col-6 col-lg-2"
          :label="translations.fieldSections.timelineSubSection.startingInfo.fields.month.label"
          :initial-data="initialData.data.serviceBegin.month"
          :error-message="
            translations.fieldSections.timelineSubSection.startingInfo.fields.month
              .requiredFieldMessage
          "
          :options="settings.monthValues"
          :default-option="settings.selectDefaultOption"
        />

        <HonoreeFormSelectField
          id="starting-year"
          v-model="formData.nomineeInfo.serviceYear"
          class="col-6 col-lg-2"
          :label="translations.fieldSections.timelineSubSection.startingInfo.fields.year.label"
          :initial-data="initialData.data.serviceBegin.year"
          :error-message="
            translations.fieldSections.timelineSubSection.startingInfo.fields.year
              .requiredFieldMessage
          "
          :options="yearsArray"
          :default-option="settings.selectDefaultOption"
        />
      </fieldset>

      <hr class="honoree-form__hr" />

      <div class="honoree-form__buttons-wrapper">
        <button
          class="wow-button wow-button--black"
          type="button"
          @click.prevent="handleFormSave"
          v-text="formLabels.saveButton.label"
        />
        <button
          :disabled="!formData.nomineeInfo.serviceNow"
          class="wow-button wow-button--black"
          :class="{ 'wow-button--disabled': !formData.nomineeInfo.serviceNow }"
          @click="triggerValidation"
          v-text="`${formLabels.nextButton.label} >`"
        />
      </div>
      <div>
        <a
          class="wow-button"
          type="button"
          :href="settings.linkToNominationPage"
          v-text="`< ${formLabels.returnToNominations.label}`"
        />
      </div>
    </form>
  </section>
</template>

<script>
import { nextTick } from 'vue';
import eventBus from '@refapp/eventbus-js';
import HonoreeFormInputField from './HonoreeFormInputField.vue';
import HonoreeFormTextAreaField from './HonoreeFormTextAreaField.vue';
import HonoreeFormSelectField from './HonoreeFormSelectField.vue';
import { VALIDATE_CHILD_EVENT, axiosPostWrapper, PERSONAL_PAGE, QUESTIONS_PAGE } from './settings';

export default {
  name: 'HonoreeFormPersonalPage',
  components: {
    HonoreeFormInputField,
    HonoreeFormSelectField,
    HonoreeFormTextAreaField,
  },
  props: {
    allowedSteps: {
      required: true,
      type: Number,
    },
    settings: {
      required: true,
      type: Object,
    },
    translations: {
      required: true,
      type: Object,
    },
    formLabels: {
      required: true,
      type: Object,
    },
    formId: {
      required: true,
      type: String,
    },
    initialData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      isSelf: this.initialData.data.nominatingYourself,
      otherRelatedVisible: false,
      otherNomineeOrganizationRoleIsVisible: true,
      formData: {
        nominatorInfo: {
          title: this.initialData.data.yourContactInfo.title,
          firstName: this.initialData.data.yourContactInfo.firstName,
          lastName: this.initialData.data.yourContactInfo.lastName,
          primaryPhone: this.initialData.data.yourContactInfo.primaryPhone,
          organizationName: this.initialData.data.yourContactInfo.organizationName,
          jobTitle: this.initialData.data.yourContactInfo.jobTitle,
          email: this.initialData.data.yourContactInfo.emailAddress,
        },
        nomineeInfo: {
          title: this.initialData.data.nomineeContactInfo.title,
          firstName: this.initialData.data.nomineeContactInfo.firstName,
          lastName: this.initialData.data.nomineeContactInfo.lastName,
          primaryPhone: this.initialData.data.nomineeContactInfo.primaryPhone,
          city: this.initialData.data.nomineeContactInfo.city,
          stateCode: this.initialData.data.nomineeContactInfo.state,
          zip: this.initialData.data.nomineeContactInfo.zipCode,
          email: this.initialData.data.nomineeContactInfo.emailAddress,
          ageRangeId: this.initialData.data.nomineeContactInfo.ageRange,

          serviceYear: this.initialData.data.serviceBegin.year,
          serviceMonth: this.initialData.data.serviceBegin.month,
          serviceNow: this.initialData.data.isCurrentlyParticipating,

          Family: this.initialData.data.nomineeContactInfo.family,
          Friends: this.initialData.data.nomineeContactInfo.friends,
          Coworkers: this.initialData.data.nomineeContactInfo.coworkers,
          Others: this.initialData.data.nomineeContactInfo.others,
          pleaseDescribe: this.initialData.data.nomineeContactInfo.pleaseDescribe
            ? this.initialData.data.nomineeContactInfo.pleaseDescribe
            : '',
          organizationName: this.initialData.data.nomineeContactInfo.organizationName,
          organizationNonprofit501c3:
            this.initialData.data.nomineeContactInfo.organizationNonprofit501c3,
          organizationFounderOrCoFounder:
            this.initialData.data.nomineeContactInfo.organizationFounderOrCoFounder,
          organizationWebsite: this.initialData.data.nomineeContactInfo.organizationWebsite,
          organizationRole: this.initialData.data.nomineeContactInfo.organizationRole,
          organizationRoleOtherInputFieldValue:
            this.initialData.data.nomineeContactInfo.organizationRoleOtherInputFieldValue,
          organizationCity: this.initialData.data.nomineeContactInfo.organizationCity,
          organizationState: this.initialData.data.nomineeContactInfo.organizationState,
          organizationPhone: this.initialData.data.nomineeContactInfo.organizationPhone,
          organizationPrimarySocialMediaLink:
            this.initialData.data.nomineeContactInfo.organizationPrimarySocialMediaLink,
          organizationAnnualBudget:
            this.initialData.data.nomineeContactInfo.organizationAnnualBudget,
          organizationDescription: this.initialData.data.nomineeContactInfo.organizationDescription
            ? this.initialData.data.nomineeContactInfo.organizationDescription
            : '',
        },
      },
      yearsArray: [],
      currentYear: new Date(),
      formReadyToGoNext: false,
      step: PERSONAL_PAGE,
      honoreeRelated: null,
      pleaseDescribeLabel: 'Please Describe:',
      pleaseDescribeError: 'Other required',

      nomineeRelationSectionRadioButtonsArePristine: true,
      showNomineeRelationSectionErrorMessage: false,

      nomineeOrganizationRoleModel: 'Other',
      nomineeOrganizationRoleSectionRadioButtonsArePristine: true,
      showNomineeOrganizationRoleSectionErrorMessage: false,

      nomineeRelationOptions: [
        {
          id: 'Family',
          label: 'Family member',
          value: 'Family',
        },
        {
          id: 'Friend',
          label: 'Friend',
          value: 'Friend',
        },
        {
          id: 'Coworker_volunteer',
          label: 'Coworker and/or volunteer',
          value: 'Coworker_volunteer',
        },
        {
          id: 'Other',
          label: 'Other',
          value: 'Other',
        },
      ],
    };
  },
  computed: {
    nomineeRelationSectionRadioButtonIsValid() {
      if (this.showNomineeRelationSectionErrorMessage) {
        return false;
      } else if (
        this.nomineeRelationSectionRadioButtonsArePristine &&
        this.honoreeRelated?.length === 0
      ) {
        return true;
      } else {
        return this.honoreeRelated?.length !== 0;
      }
    },
    nomineeOrganizationRoleSectionRadioButtonIsValid() {
      if (this.showNomineeOrganizationRoleSectionErrorMessage) {
        return false;
      } else if (
        this.nomineeOrganizationRoleSectionRadioButtonsArePristine &&
        this.nomineeOrganizationRoleModel?.length === 0
      ) {
        return true;
      } else {
        return this.nomineeOrganizationRoleModel?.length !== 0;
      }
    },
  },
  watch: {
    isSelf() {
      this.resetNominatorInfo();
    },

    honoreeRelated(value) {
      switch (value) {
        case 'Family':
          this.otherRelatedVisible = false;
          this.formData.nomineeInfo.Friends = 0;
          this.formData.nomineeInfo.Coworkers = 0;
          this.formData.nomineeInfo.Others = 0;
          this.formData.nomineeInfo.Family = this.nomineeRelationOptions[0].label;
          break;
        case 'Friend':
          this.otherRelatedVisible = false;
          this.formData.nomineeInfo.Friends = this.nomineeRelationOptions[1].label;
          this.formData.nomineeInfo.Coworkers = 0;
          this.formData.nomineeInfo.Others = 0;
          this.formData.nomineeInfo.Family = 0;
          break;
        case 'Coworker_volunteer':
          this.otherRelatedVisible = false;
          this.formData.nomineeInfo.Friends = 0;
          this.formData.nomineeInfo.Coworkers = this.nomineeRelationOptions[2].label;
          this.formData.nomineeInfo.Others = 0;
          this.formData.nomineeInfo.Family = 0;

          break;
        case 'Other':
          this.otherRelatedVisible = true;
          this.formData.nomineeInfo.Friends = 0;
          this.formData.nomineeInfo.Coworkers = 0;
          this.formData.nomineeInfo.Others = this.nomineeRelationOptions[3].label;
          this.formData.nomineeInfo.Family = 0;
          break;
        default:
          this.otherRelatedVisible = false;
          this.formData.nomineeInfo.Friends = 0;
          this.formData.nomineeInfo.Coworkers = 0;
          this.formData.nomineeInfo.Others = 0;
          this.formData.nomineeInfo.Family = 0;
      }
    },

    nomineeOrganizationRoleModel(value) {
      if (value === 'Other') {
        this.otherNomineeOrganizationRoleIsVisible = true;
        this.formData.nomineeInfo.organizationRole = '';
      } else {
        this.otherNomineeOrganizationRoleIsVisible = false;
        this.formData.nomineeInfo.organizationRole = value;
      }
    },
  },
  mounted() {
    for (let i = this.settings.firstYearDropdown; i <= this.currentYear.getUTCFullYear(); i++) {
      this.yearsArray.push({
        label: `${i}`,
        id: i,
        code: i,
      });
    }
    this.yearsArray.reverse();

    let relatedData = this.initialData.data.nomineeContactInfo;
    if (relatedData.family && relatedData.family.length > 1) {
      this.honoreeRelated = 'Family';
    } else if (relatedData.coworkers && relatedData.coworkers.length > 1) {
      this.honoreeRelated = 'Coworker_volunteer';
    } else if (relatedData.friends && relatedData.friends.length > 1) {
      this.honoreeRelated = 'Friend';
    } else if (
      (relatedData.others && relatedData.others.length > 1) ||
      relatedData.pleaseDescribe
    ) {
      this.honoreeRelated = 'Other';
      this.otherRelatedVisible = true;
    } else {
      this.honoreeRelated = '';
      this.otherRelatedVisible = false;
    }
  },
  methods: {
    updateNomineeRelationSectionRadioButtonsState() {
      this.nomineeRelationSectionRadioButtonsArePristine = false;
      this.showNomineeRelationSectionErrorMessage = false;
    },
    updateNomineeOrganizationRoleRadioButtonsState() {
      this.nomineeOrganizationRoleSectionRadioButtonsArePristine = false;
      this.showNomineeOrganizationRoleSectionErrorMessage = false;
    },
    resetNominatorInfo() {
      this.formData.nominatorInfo.title = null;
      this.formData.nominatorInfo.firstName = '';
      this.formData.nominatorInfo.lastName = '';
      this.formData.nominatorInfo.primaryPhone = '';
      this.formData.nominatorInfo.organizationName = '';
      this.formData.nominatorInfo.jobTitle = '';
      this.formData.nominatorInfo.email = '';
    },
    handleFormSubmit(e) {
      e.preventDefault();
      eventBus.emit(VALIDATE_CHILD_EVENT);

      nextTick().then(() => {
        const formValid = this.isSelf
          ? this.validateObject(this.formData.nomineeInfo)
          : this.validateObject(this.formData.nomineeInfo) &&
            this.validateObject(this.formData.nominatorInfo);

        if (formValid) {
          const data = this.gatherData(true);
          this.submitData(data);
        }
      });
    },
    triggerValidation() {
      eventBus.emit(VALIDATE_CHILD_EVENT);
    },

    handleFormSave() {
      eventBus.emit(VALIDATE_CHILD_EVENT);
      nextTick().then(() => {
        const data = this.gatherData();
        this.submitData(data);
      });
    },
    gatherData(incrementLocalStep = false) {
      const incrementAllowedStep = () => {
        return incrementLocalStep && this.step >= this.allowedSteps;
      };

      const data = {
        isSelf: this.isSelf,
        nominationId: this.formId,
        step: this.step,
        incrementLocalStep,
        incrementStep: incrementAllowedStep(),
        nomineeInfo: this.extractData(this.formData.nomineeInfo),
      };

      if (!this.isSelf) {
        data.nominatorInfo = this.extractData(this.formData.nominatorInfo);
      }

      return data;
    },
    extractData(object) {
      const keys = Object.keys(object);
      const data = {};
      keys.forEach((key) => {
        if (typeof object[key] === 'object') {
          data[key] = object[key].value;
        } else {
          data[key] = object[key];
        }
      });
      return data;
    },
    validateObject(object) {
      const keys = Object.keys(object);
      let formValid = true;

      keys.forEach((key) => {
        if (typeof object[key] === 'object' && !object[key].isValid) {
          formValid = false;
        }
      });

      if (!this.isSelf && !this.honoreeRelated.length) {
        formValid = false;
        this.showNomineeRelationSectionErrorMessage = true;
      }

      return formValid;
    },
    submitData(data) {
      axiosPostWrapper(this.settings.webserviceUrl, JSON.stringify(data)).then((response) => {
        if (data.incrementLocalStep) this.$emit('nextClick', QUESTIONS_PAGE);
        this.$emit('onDataSubmit', response);
      });
    },
  },
};
</script>

<style>
/* Enter and leave animations can use different */

/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.25s ease;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>
